import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';
import { EventIQMockup } from '../components/ProductMockups';

const features = [
  { title: 'Conferences & summits', body: 'Multi-track event operations with speaker, sponsor, and session management.' },
  { title: 'Hybrid & virtual', body: 'Native Zoom and Microsoft Teams for hybrid experiences with automatic attendance tracking.' },
  { title: 'Trade shows', body: 'Booth logistics, staff scheduling, and lead capture that flows into your CRM.' },
  { title: 'Internal events', body: 'Offsites, all-hands, and training — with attendee lists, agendas, and feedback.' },
  { title: 'Attendee experience', body: 'Registration, agendas, and networking in a branded, mobile-friendly portal.' },
  { title: 'Post-event analytics', body: 'Attendance, engagement, and pipeline influence — attributed by session.' },
];

const EventIQPage = ({ onOpenDemo }) => {
  return (
    <>
      <SEO
        title="EventIQ — Corporate Event Management"
        description="EventIQ is the corporate event management platform for conferences, summits, trade shows, and hybrid events. Attendee experience and real-time analytics in one platform."
        path="/eventiq"
        keywords={['corporate event management', 'event management platform', 'hybrid events', 'trade show management']}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'EventIQ', path: '/eventiq' },
        ]}
        product={{
          name: 'EventIQ',
          description: 'Corporate event management platform for conferences, summits, and hybrid events.',
          category: 'BusinessApplication',
        }}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">EventIQ</div>
          <h1 className="headline-xl text-ink mb-6">
            Corporate events,<br />
            <span className="text-ink-3">from planning to pipeline.</span>
          </h1>
          <p className="text-lg md:text-xl text-ink-2 max-w-2xl mx-auto leading-relaxed mb-9">
            Manage conferences, summits, trade shows, and hybrid events with logistics automation, attendee experience, and real-time analytics.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-14">
            <button onClick={onOpenDemo} className="btn-primary">
              Request a demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/pricing" className="btn-link">
              View pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6">
          <EventIQMockup />
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">Capabilities</div>
            <h2 className="headline-lg text-ink">Every event, one platform.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line-2)] rounded-3xl overflow-hidden border border-[var(--line-2)]">
            {features.map((f) => (
              <div key={f.title} className="bg-white p-8 lg:p-10">
                <Check className="w-5 h-5 text-ink mb-5" strokeWidth={2} />
                <h3 className="text-lg font-semibold text-ink mb-3">{f.title}</h3>
                <p className="text-sm text-ink-2 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0F172A] dark section relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="headline-lg text-white mb-6">See EventIQ in action.</h2>
          <div className="flex flex-wrap gap-4 justify-center">
            <button onClick={onOpenDemo} className="btn-invert">
              Request a demo <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/contact" className="btn-invert-outline">Contact sales</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default EventIQPage;
